import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperience,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Divider, Typography } from '@material-ui/core';
import { debounce, flatten, uniq } from 'lodash';
import { ExternalLink, Users } from 'lucide-react';
import moment from 'moment-timezone';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColoredChipLight } from '../../../../../components/ColoredChip';
import { Loader } from '../../../../../components/Loader';
import {
  SlidingCardContainer,
  SLIDING_CARD_CONTAINER_HEIGHT
} from '../../../../../components/SlidingCardContainer';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { useRoutes, useStringQueryParam } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import { useExperienceInstancesCurrentlyRelevant } from '../../../../../services/experiences/instance';
import {
  usePeopleTabV2Enabled,
  useTimelineItemPseudoInstances
} from '../../../../../services/experiences/timelineItemStatus';
import { getItemDisplayName, resolveExpTitle } from '../../../helpers/adminName';
import { getMostRecentSuccessfulConcreteMessageOrAnyOther } from '../../../../../services/experiences/message';
import { getTaskTemplateById } from '../../../../../services/experiences/template';
import { useProductAnalytics } from '../../../../../services/productAnalytics';
import { Timestamp, toMoment } from '../../../../../services/time';
import { CardItemDialog } from '../../../components/CalendarV2/CardItemDialog';
import { CalV2TimelineItemExecutionTable } from '../../../components/CalendarV2/DialogTable';
import { OnHover } from '../../../components/ExecutionReportTable/Tasks';
import { StatusChipCount } from '../../../components/StatusChipCount';
import { ParticipantDialog, ParticipantDialogLazy } from '../ParticipantDialog';
import { ActorLegend } from '../ParticipantDialogComponents/ActorLegend';
import {
  aggregateForAllDates,
  CAL_V2_AGGREGATION_DATE_FORMAT
} from './aggregate';
import { CalV2Item } from './types';

const ItemContainerWrapper = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  ':hover': {
    cursor: 'pointer'
  }
}));

const TitleItem = styled(Typography)((p) => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));

const SectionTitle = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  gap: `${p.theme.spacing(0.5)}px`,
  position: 'sticky',
  top: 0,
  left: 0,
  background: 'white',
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(3)}px ${p.theme.spacing(
    0
  )}px ${p.theme.spacing(3)}px`
}));

const SectionTitleDay = styled(Typography)((p) => ({
  color: p.theme.custom.colors.gray.w400,
  fontWeight: p.theme.typography.fontWeightLight
}));

const SectionToday = styled('div')`
  margin-left: auto !important;
`;

const SectionTitleToday = styled(Typography)((p) => ({
  color: p.theme.custom.colors.yellow.w700
}));

const UserIconItem = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  color: p.theme.custom.colors.gray.w500,
  gap: `${p.theme.spacing(0.5)}px`,
  flexWrap: 'nowrap',
  marginLeft: 'auto',
  marginRight: '8px'
}));

const EmptySchedule = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: p.theme.spacing(2),
  minHeight: p.theme.spacing(25),
  backgroundColor: p.theme.custom.colors.gray.w100,
  color: p.theme.custom.colors.gray.w500
}));

const StatusCount = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: p.theme.spacing(0.5),
  flex: '2 1 auto'
}));

const TableLeftContainer = styled('div')((p) => ({
  display: 'flex',
  width: '92%',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: p.theme.spacing(1),
  marginBottom: p.theme.spacing(0)
}));

type Actors = {
  [key: string]: Actor;
};

type Actor = { key: string; name: string; color: string };

const cohortLabelOrgDate = (date: string) => {
  const d = date.slice(0, 4);
  if (d.includes('-') || d.includes('/') || d.includes('.')) {
    return date.slice(0, -5);
  }
  return date.slice(5);
};

const subHeadingDateLabel = (scheduledDate: Timestamp, itemType: string) => {
  const format = toMoment(scheduledDate).format(CAL_V2_AGGREGATION_DATE_FORMAT);
  const diff = moment(format).diff(
    moment().format(CAL_V2_AGGREGATION_DATE_FORMAT),
    'days'
  );
  if (diff < 0) {
    switch (itemType) {
      case 'automation':
        return 'Ran on: ';
      case 'task':
        return 'Started on: ';
      case 'message':
        return 'Sent on: ';
      default:
        return;
    }
  } else {
    switch (itemType) {
      case 'automation':
        return 'Will run on: ';
      case 'task':
        return 'Will start on: ';
      case 'message':
        return 'Will send on: ';
      default:
        return;
    }
  }
};

const GroupContainerLabel = (date: string) => {
  const mom = moment(date, CAL_V2_AGGREGATION_DATE_FORMAT);
  const isSameDate = mom.isSame(moment(), 'day');
  return (
    <SectionTitle>
      <SectionTitleDay> {moment(date).format('dddd')} </SectionTitleDay>
      <Typography variant="h6">{moment(date).format('MMMM DD')}</Typography>
      {isSameDate && (
        <SectionToday>
          <ColoredChipLight color="yellow" variant="squared">
            <SectionTitleToday> Today </SectionTitleToday>
          </ColoredChipLight>
        </SectionToday>
      )}
    </SectionTitle>
  );
};

const GroupItemLabel = (date: string, items: CalV2Item[]) => {
  const cohortLabel = cohortLabelOrgDate(date);
  const { t } = useUnsafeTranslation();

  const instanceCountPerCohort = uniq(
    flatten(
      items.map((item) => {
        return item.instanceIds;
      })
    )
  ).length;
  return (
    <>
      <Typography variant="h6" color="textSecondary">
        {cohortLabel} {t('Cohort')}
      </Typography>
      <UserIconItem>
        {' '}
        <Users size="16" />
        {instanceCountPerCohort !== 0 && instanceCountPerCohort}{' '}
      </UserIconItem>
    </>
  );
};

type CalendarV2Ref = { scrollToStartingPosition: () => void };

const CalendarV2Inner = React.forwardRef<
  CalendarV2Ref,
  {
    tpl: Doc<IExperienceTemplate>;
    instances: Doc<IExperience>[] | undefined;
    loading: boolean;
  }
>(({ tpl, instances, loading }, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { trackEvent } = useProductAnalytics();
  // When the projection feeds the Schedule, rows are pseudo-instances that lack
  // the full timing/audience the big ParticipantDialog reads — re-fetch the one
  // full instance via ParticipantDialogLazy (matches Participants/ExecutionReportTab).
  const peopleTabV2Enabled = usePeopleTabV2Enabled();

  const [dialogInstanceId, setDialogInstanceId] = useStringQueryParam('i', '');
  const setDialogInstance = useCallback(
    (inst: Doc<IExperience>) => setDialogInstanceId(inst.id),
    [setDialogInstanceId]
  );

  const _trackCardScroll = (count: number) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      'Experience Template ID': tpl.id,
      name: 'Schedule Card Scrolled',
      Explanation: 'Admin user scrolls within one card in the schedule',
      'Items in Card': count
    });
  };
  const trackCardScroll = useMemo(() => debounce(_trackCardScroll, 5000), []);

  const trackContainerScrollEvent = (
    start: number,
    end: number,
    scrollDistance: number
  ) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      'Experience Template ID': tpl.id,
      name: 'Schedule Scrolled',
      Explanation: 'Admin user scrolls the schedule horizontally',
      'Scroll Index Left': start,
      'Scroll Index Right': end,
      'Scroll Distance': scrollDistance
    });
  };
  const dialogInstance =
    dialogInstanceId && instances
      ? instances.find((inst) => inst.id === dialogInstanceId) || null
      : null;

  const data = useMemo(() => {
    if (!instances) {
      return null;
    }
    return aggregateForAllDates(instances, tpl.data.tz, tpl);
  }, [tpl, instances]);

  if (loading) {
    return <Loader height={SLIDING_CARD_CONTAINER_HEIGHT} />;
  }

  if (!instances || !data) return null; //error

  const actors: Actors = {
    participant: {
      key: PARTICIPANT_ACTOR_KEY,
      name: t('Participant'),
      color: theme.custom.colors.blue.w400
    },
    ...tpl.data.supportingActors
  };
  const renderItem = (item: CalV2Item) => {
    if (item.type === 'task' && item.instanceIds.length) {
      const task = getTaskTemplateById(tpl, item.itemTplId);
      return (
        task && (
          <CardItem
            key={task.id}
            renderedItem={item}
            tpl={tpl}
            instances={instances}
            title={resolveExpTitle(getItemDisplayName(task), tpl.data.title)}
            actor={actors[task.actor]}
            color={actors[task.actor].color}
            setDialogInstance={setDialogInstance}
          />
        )
      );
    }
    if (item.type === 'automation' && item.instanceIds.length) {
      const automation = tpl.data.automations.find(
        (aut) => aut.id === item.itemTplId
      );
      return (
        automation && (
          <CardItem
            key={automation.id}
            renderedItem={item}
            instances={instances}
            tpl={tpl}
            title={automation.title}
            color={theme.custom.colors.purple.w500}
            actor={{
              key: PARTICIPANT_ACTOR_KEY,
              name: t('Participant'),
              color: theme.custom.colors.purple.w500
            }}
            setDialogInstance={setDialogInstance}
          />
        )
      );
    }
    if (item.type === 'message' && item.instanceIds.length) {
      const message = tpl.data.messages.other.find(
        (msg) => msg.id === item.itemTplId
      );
      return (
        message && (
          <CardItem
            key={message.id}
            renderedItem={item}
            instances={instances}
            tpl={tpl}
            title={resolveExpTitle(getItemDisplayName(message), tpl.data.title)}
            actor={actors[message.actor]}
            color={actors[message.actor].color}
            setDialogInstance={setDialogInstance}
          />
        )
      );
    }
    return null;
  };
  return (
    <>
      {instances && !instances.length && (
        <EmptySchedule>No items to display</EmptySchedule>
      )}

      {instances && !!instances.length && (
        <SlidingCardContainer
          containers={data}
          renderItem={renderItem}
          GroupContainerLabel={GroupContainerLabel}
          GroupItemLabel={GroupItemLabel}
          onScrollVerticallyWithinOneCard={trackCardScroll}
          onScrollHorizontallyWithinContainer={(
            leftMostCardIndex,
            rightMostCardIndex,
            scrollDistance
          ) => {
            trackContainerScrollEvent(
              leftMostCardIndex,
              rightMostCardIndex,
              scrollDistance
            );
          }}
          ref={ref}
        />
      )}
      {peopleTabV2Enabled
        ? dialogInstanceId && (
            <ParticipantDialogLazy
              instanceId={dialogInstanceId}
              tplId={tpl.id}
              open={true}
              onClose={() => setDialogInstanceId('')}
            />
          )
        : dialogInstance && (
            <ParticipantDialog
              open={true}
              tpl={tpl}
              d={dialogInstance}
              onClose={() => setDialogInstanceId('')}
            />
          )}
    </>
  );
});

// Data-source split (mirrors Participants.tsx): the projection-backed V2 path and
// the full-instance V1 path each mount only their own listener, gated by the flag.
const CalendarV2V1 = React.forwardRef<CalendarV2Ref, { tpl: Doc<IExperienceTemplate> }>(
  ({ tpl }, ref) => {
    const [instances, loading] = useExperienceInstancesCurrentlyRelevant(
      tpl.data.orgId,
      tpl.id,
      tpl.data.expectedEndThreshold
    );
    return (
      <CalendarV2Inner
        ref={ref}
        tpl={tpl}
        instances={instances || undefined}
        loading={loading}
      />
    );
  }
);

const CalendarV2V2 = React.forwardRef<CalendarV2Ref, { tpl: Doc<IExperienceTemplate> }>(
  ({ tpl }, ref) => {
    const [instances, loading] = useTimelineItemPseudoInstances(
      tpl.data.orgId,
      tpl.id,
      tpl.data.expectedEndThreshold
    );
    return (
      <CalendarV2Inner
        ref={ref}
        tpl={tpl}
        instances={instances || undefined}
        loading={loading}
      />
    );
  }
);

export const CalendarV2 = React.forwardRef<
  CalendarV2Ref,
  { tpl: Doc<IExperienceTemplate> }
>((props, ref) =>
  usePeopleTabV2Enabled() ? (
    <CalendarV2V2 ref={ref} {...props} />
  ) : (
    <CalendarV2V1 ref={ref} {...props} />
  )
);

const CardItem = ({
  renderedItem,
  title,
  instances,
  color,
  tpl,
  actor,
  setDialogInstance
}: {
  renderedItem: CalV2Item;
  title: string;
  color: string;
  tpl: Doc<IExperienceTemplate>;
  instances: Doc<IExperience>[];
  actor: Actor;
  setDialogInstance: (inst: Doc<IExperience>) => void;
}) => {
  const innerDialog = useDialogState(false);
  const { trackEvent } = useProductAnalytics();
  const handleTrackEventTimelineItem = () => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      'Experience Template ID': tpl.id,
      name: 'Schedule Card Timeline Item Clicked',
      Explanation:
        'Admin user clicks on a timeline item appearing in the schedule card',
      'Timeline Item ID': renderedItem.itemTplId,
      'Timeline Item Title': title,
      'Timeline Item Type': renderedItem.type,
      'Done Count': renderedItem.counts.success,
      'Error Count':
        renderedItem.type !== 'task' ? renderedItem.counts.error : 0,
      'To Do Count': renderedItem.counts.ongoing,
      'Upcoming Count': renderedItem.counts.upcoming,
      'Overdue Count':
        renderedItem.type === 'task' ? renderedItem.counts.error : 0
    });
  };
  return (
    <FlexContainer spacing={1} direction="column" marginBottom={2}>
      <ItemContainerWrapper>
        <FlexContainer
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          fullWidth
          onClick={() => {
            innerDialog.openDialog();
            handleTrackEventTimelineItem();
          }}
        >
          <FlexContainer overflowX="auto" spacing={0.5}>
            <ActorLegend type={renderedItem.type} color={color} />
            <TitleItem>{title}</TitleItem>
          </FlexContainer>
          <StatusCount>
            {Object.entries(renderedItem.counts).map(([k, count]) => {
              return (
                count > 0 && (
                  <StatusChipCount key={k} status={k}>
                    {count}
                  </StatusChipCount>
                )
              );
            })}
          </StatusCount>
        </FlexContainer>
      </ItemContainerWrapper>
      <Divider light style={{ width: '100%' }} />
      {innerDialog.dialogOpen && (
        <CardItemDialog
          open={innerDialog.dialogOpen}
          onClose={innerDialog.closeDialog}
          headerLeft={
            <DialogHeaderTitle
              color={color}
              actor={actor.name}
              title={title}
              tpl={tpl}
              instances={instances}
              renderedItem={renderedItem}
            />
          }
          dialogTable={
            <CalV2TimelineItemExecutionTable
              renderedItem={renderedItem}
              instances={instances}
              tpl={tpl}
              itemTplId={renderedItem.itemTplId}
              onShowInstanceDetails={(d) => {
                trackEvent({
                  eventType: PAEventType.EXPERIENCE_INSTANCE,
                  name: 'Admin Instance View Loaded',
                  'Instance ID': d.id,
                  Trigger: 'Schedule Modal',
                  Explanation:
                    'Admin user views the details of a single instance'
                });
                setDialogInstance(d);
              }}
              actor={{ key: actor.key, label: actor.name }}
            />
          }
        />
      )}
    </FlexContainer>
  );
};

const DialogHeaderTitle = ({
  color,
  actor,
  title,
  tpl,
  instances,
  renderedItem
}: {
  actor?: string;
  color: string;
  title: string;
  instances: Doc<IExperience>[];
  renderedItem: CalV2Item;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const currentUser = useCurrentUser();
  const { org } = currentUser;
  const { ROUTES, goTo } = useRoutes();

  const scheduledDate = useMemo(() => {
    const instance = instances.filter((instance) => {
      return renderedItem.instanceIds.includes(instance.id);
    })[0];

    if (renderedItem.type === 'task') {
      return instance.data.tasks.find(
        (task) => task.templateId === renderedItem.itemTplId
      )?.timing.start;
    }
    if (renderedItem.type === 'automation') {
      return instance.data.automations[renderedItem.itemTplId]?.timing.at;
    }
    if (renderedItem.type === 'message') {
      // Show the ACTUAL send date (concrete record's `at`) for sent messages,
      // falling back to the scheduled `timing.at`, so the header matches the
      // Schedule's actual-send bucketing.
      const concrete = getMostRecentSuccessfulConcreteMessageOrAnyOther(
        renderedItem.itemTplId,
        instance?.data.messages ?? [],
        instance?.data.messagesStatuses ?? {}
      );
      const actualSendAt = concrete && 'at' in concrete ? concrete.at : null;
      return (
        actualSendAt ??
        instance?.data.messagesStatuses[renderedItem.itemTplId]?.timing.at
      );
    }
  }, [instances, renderedItem]);

  const params = useMemo(() => {
    if (renderedItem.type === 'task') {
      return {
        type: 'task' as const,
        taskId: renderedItem.itemTplId
      };
    }
    if (renderedItem.type === 'message') {
      return {
        type: 'msg' as const,
        msgId: renderedItem.itemTplId
      };
    }
    if (renderedItem.type === 'automation') {
      return {
        type: 'automation' as const,
        automationId: renderedItem.itemTplId
      };
    }
  }, [renderedItem]);
  return (
    <TableLeftContainer>
      <FlexContainer
        spacing={1}
        justifyContent="flex-start"
        alignItems="center"
        fullWidth
      >
        <ActorLegend type={renderedItem.type} color={color} flex={'none'}>
          <Typography variant="subtitle2">
            {renderedItem.type === 'automation' ? 'Automation' : actor}
          </Typography>
        </ActorLegend>
        <OnHover
          onClick={() =>
            goTo(ROUTES.experiences.details.general.url(tpl.id, params))
          }
        >
          <Typography variant="h4">{title}</Typography>
          <div className="showOnHover">
            <ExternalLink size={16} />
          </div>
        </OnHover>
      </FlexContainer>
      {scheduledDate && (
        <Typography variant="caption" color="textSecondary">
          {subHeadingDateLabel(scheduledDate, renderedItem.type)}
          {toMoment(scheduledDate)
            .tz(tpl.data.tz)
            .format(`${org.dateFormats.MMDDYYYY}  HH:mm`)}{' '}
          {toMoment(scheduledDate).tz(tpl.data.tz).format('z')}
        </Typography>
      )}
    </TableLeftContainer>
  );
};
